import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ToothFairyModal from "../../../../Components/ToothFairyComponents/ToothFairyModal";
import ToolTestPanel from "./ToolTestPanel";
import { isLargeScreen } from "../../../MainViewContainer";

/**
 * ToolTestModal — isolated tool / hook test UI.
 *
 * Thin modal wrapper around ToolTestPanel (which carries the actual arguments
 * editor + run/result UI, backed by the ChatECS `POST /test/tool` and
 * `POST /test/hook` endpoints via `API/ToolTesting`). The panel is remounted
 * (React `key`) every time the modal opens or the target id / prefill changes,
 * so the arguments editor re-initialises with freshly pre-filled values and the
 * previous result is cleared.
 *
 * Props:
 *   isModalOpen, setIsModalOpen  — modal visibility
 *   mode                         — "tool" | "hook"
 *   id                           — functionId (tool) or environmentId (hook)
 *   token                        — workspace API key (x-api-key)
 *   entityName                   — optional label for the title
 *   parameters                   — function's `parameters` JSON schema (tool mode)
 *   staticArgs                   — function's `staticArgs` (tool mode)
 */
const ToolTestModal = ({
  isModalOpen,
  setIsModalOpen,
  mode = "tool",
  id,
  token,
  entityName,
  parameters,
  staticArgs,
}) => {
  const { t } = useTranslation();
  const [panelKey, setPanelKey] = useState(0);

  useEffect(() => {
    if (isModalOpen) {
      setPanelKey((k) => k + 1);
    }
  }, [isModalOpen, id, parameters, staticArgs]);

  const isTool = mode === "tool";
  const title = isTool
    ? t("tools.testToolTitle", { defaultValue: "Test Tool" })
    : t("tools.testHookTitle", { defaultValue: "Test Hook" });

  return (
    <ToothFairyModal
      isModalOpen={isModalOpen}
      setIsModalOpen={setIsModalOpen}
      modalTitle={`${title}${entityName ? ` — ${entityName}` : ""}`}
      modalStyle={{
        maxHeight: "90%",
        height: "auto",
        maxWidth: isLargeScreen() ? 760 : 380,
        marginHorizontal: "auto",
        marginTop: "auto",
        marginBottom: "auto",
        margin: "auto",
      }}
      closeModal={() => {
        setIsModalOpen(false);
      }}
    >
      <ToolTestPanel
        key={panelKey}
        mode={mode}
        id={id}
        token={token}
        parameters={parameters}
        staticArgs={staticArgs}
      />
    </ToothFairyModal>
  );
};

export default ToolTestModal;
